if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Membership products (e.g. DML on Demand) allow one plan per cart:
        // check the cart first and explain instead of adding a second plan.
        if (this.dataset.exclusiveProduct) {
          this.handleErrorMessage();
          this.toggleViewCartLink(false);
          // Block repeat clicks while the cart is checked.
          this.submitButton.setAttribute('aria-disabled', true);
          this.checkExclusivePlan().then((conflict) => {
            this.submitButton.removeAttribute('aria-disabled');
            if (conflict) {
              this.handleErrorMessage(conflict);
              this.toggleViewCartLink(true);
            } else {
              this.addToCart(evt);
            }
          });
          return;
        }

        this.addToCart(evt);
      }

      // "View cart" shown with the one-plan-per-cart error, so the customer can
      // see and remove the plan already in the cart.
      toggleViewCartLink(show) {
        const link = this.querySelector('[data-open-cart]');
        if (link) link.hidden = !show;
      }

      checkExclusivePlan() {
        const productId = Number(this.dataset.exclusiveProduct);
        const variantId = Number(new FormData(this.form).get('id'));

        return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            const existing = cart.items.find((item) => item.product_id === productId);
            if (!existing) return null;

            const current = this.dataset.exclusiveCurrent || '';
            // Prefer the plan names shown on the page (e.g. Yearly -> Annual).
            let titles = {};
            try {
              titles = JSON.parse(this.dataset.exclusiveTitles || '{}');
            } catch (e) {}
            const existingName =
              titles[(existing.variant_title || '').trim().toLowerCase()] || existing.variant_title || existing.product_title;
            const template =
              existing.variant_id === variantId ? this.dataset.exclusiveSameMessage : this.dataset.exclusiveOtherMessage;
            return (template || '')
              .replaceAll('[current]', current)
              .replaceAll('[existing]', existingName);
          })
          // If the cart cannot be read, fall back to the normal add.
          .catch(() => null);
      }

      addToCart(evt) {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

// "View cart" links ([data-open-cart]) open the cart drawer when the theme
// uses one, and otherwise follow their href to the cart page.
if (!window.productFormViewCartBound) {
  window.productFormViewCartBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-open-cart]');
    const drawer = document.querySelector('cart-drawer');
    if (!link || !drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(link);
  });
}
